// Importing the necessary type for the team member service response
import {
  IAttendanceServiceResponse,
  IAttendanceUpdatePayload,
  IBulkAttendanceParams,
  TeamMembersAttendanceStatusFilter,
} from "../../../features/teamMembers/attendanceList/types";
import {
  IDailyDairyReportParams,
  IDailyDairyReportServiceResponse,
} from "../../../features/teamMembers/dailyDairyReport/types";
import {
  IBulkLeaveParams,
  ILeaveActionPayload,
  ITeamMembersLeaveServiceResponse,
  TeamMembersLeaveStatusFilter,
} from "../../../features/teamMembers/leaveList/types";
import { ITeamMemberServiceResponse } from "../../../features/teamMembers/teamMembersList/types";
import { ITeamMembersConveyanceResponse } from "../../../Types/TeamMembers";
import {
  IBaseResponse,
  IStatusRepsonse,
} from "../../../Types/UserProfileDetails";
import ApiService from "../../ApiService";

enum TeamMembersEndpoints {
  MANAGER_DASHBOARD = "/manager/dashboard",
  ATTENDANCE = "/manager/team-members/attendance",
  CONVEYANCE = "/manager/team-members/conveyance",
  UPDATE_ATTENDANCE = "/employee/attendance/correction/action",
  LEAVE = "/manager/team-members/leave",
  APPROVE_LEAVE = "/employee/leave/action",
  BULK_APPROVED_ATTENDANCE_REQUESTS = "/manager/team-members/attendance/bulk-action",
  BULK_APPROVED_LEAVE_REQUESTS = "/manager/team-members/leave/bulk-action",
  DAILY_DAIRY_REPORT = "/manager/team-members/daily-dairy",
}

export interface ITeamMembersConveyanceAction {
  approval_type: string;
  status: string;
  conveyances: Array<{
    conveyances: Array<{
      id: number;
      approved_amount: number;
      remark: string;
    }>;
  }>;
}

// Define the TeamMemberService class that extends ApiService to handle team member-related API requests
class TeamMemberService extends ApiService {
  // Method to fetch team members from the dashboard
  async getTeamMembers(
    employeeId?: number
  ): Promise<ITeamMemberServiceResponse> {
    return this.get<ITeamMemberServiceResponse>(
      `${TeamMembersEndpoints.MANAGER_DASHBOARD}${
        employeeId ? `/${employeeId}` : ""
      }`
    );
  }

  // Method to fetch team member attendance based on the user parameter
  async fetchTeamMemberAttendance(
    user: string,
    activeYear: number,
    type?: string,
    status?: TeamMembersAttendanceStatusFilter
  ): Promise<IAttendanceServiceResponse> {
    const urlParams = new URLSearchParams();
    if (type) urlParams.append("type", type);
    if (activeYear && typeof activeYear === "number")
      urlParams.append("active_year", activeYear?.toString());
    if (status) urlParams.append("status", status);

    const endpoint = `${TeamMembersEndpoints.ATTENDANCE}/${
      user ? user : ""
    }?${urlParams.toString()}`; // When no user is specified, fetch for all team members

    return this.get<IAttendanceServiceResponse>(endpoint);
  }

  // Method to update the attendance status for a specific team member
  async updateAttendanceStatus(attendanceUpdateData: IAttendanceUpdatePayload) {
    return this.post<IBaseResponse>(
      TeamMembersEndpoints.UPDATE_ATTENDANCE,
      attendanceUpdateData
    );
  }

  // Method to fetch leave details of team members
  async fetchTeamMembersLeave(
    user: string,
    activeYear: number,
    type?: string,
    status?: TeamMembersLeaveStatusFilter
  ): Promise<ITeamMembersLeaveServiceResponse> {
    const urlParams = new URLSearchParams();

    if (activeYear && typeof activeYear === "number")
      urlParams.append("active_year", activeYear.toString());

    if (type) urlParams.append("type", type);
    if (status) urlParams.append("status", status);

    const endpoint = `/manager/team-members/leave/${
      user ? user : ""
    }?${urlParams.toString()}`; // Fetch leave data for all team members

    return this.get<ITeamMembersLeaveServiceResponse>(endpoint);
  }

  async fetchTeamMembersConveyance({
    employeeId,
    startDate,
    endDate,
    selectEmployeeId,
    page,
    size,
    type,
    status,
  }: {
    employeeId?: string;
    startDate?: string;
    endDate?: string;
    selectEmployeeId?: string;
    page?: number;
    size?: number;
    type?: string;
    status?: string;
  }) {
    const urlParams = new URLSearchParams();
    if (startDate) urlParams.append("start_date", startDate);
    if (endDate) urlParams.append("end_date", endDate);
    if (selectEmployeeId) urlParams.append("employee_id", selectEmployeeId);
    if (page && typeof page === "number")
      urlParams.append("page", page.toString());
    if (size && typeof size === "number")
      urlParams.append("size", size.toString());
    if (type) urlParams.append("type", type);
    if (status) urlParams.append("status", status);

    return this.get<ITeamMembersConveyanceResponse>(
      `${TeamMembersEndpoints.CONVEYANCE}/${
        employeeId ? employeeId : ""
      }?${urlParams?.toString()}`
    );
  }

  // Method to approve or reject a leave request
  async approveLeave(
    leaveApproveData: ILeaveActionPayload
  ): Promise<IBaseResponse> {
    return this.post<IBaseResponse>(
      TeamMembersEndpoints.APPROVE_LEAVE,
      leaveApproveData
    );
  }

  async bulkApprovedAttendanceRequest(
    bulkAttendanceData: IBulkAttendanceParams
  ): Promise<IBaseResponse> {
    return this.post<IBaseResponse>(
      TeamMembersEndpoints.BULK_APPROVED_ATTENDANCE_REQUESTS,
      bulkAttendanceData
    );
  }

  async bulkApproveLeaveRequest(
    bulkLeaveData: IBulkLeaveParams
  ): Promise<IBaseResponse> {
    return this.post<IBaseResponse>(
      TeamMembersEndpoints.BULK_APPROVED_LEAVE_REQUESTS,
      bulkLeaveData
    );
  }

  async dailyDairyReport(
    params: IDailyDairyReportParams
  ): Promise<IDailyDairyReportServiceResponse> {
    return this.post<IDailyDairyReportServiceResponse>(
      TeamMembersEndpoints.DAILY_DAIRY_REPORT,
      params
    );
  }

  async teamMembersConveyanceAction(
    params: ITeamMembersConveyanceAction
  ): Promise<IStatusRepsonse> {
    return this.post<IStatusRepsonse>(
      `${TeamMembersEndpoints.CONVEYANCE}/action`,
      params
    );
  }
}

// Create an instance of TeamMemberService and export it for use in other parts of the application
export const teamMemberService = new TeamMemberService();



